import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import CompanyVerificationModal from '../Verification/CompanyVerificationModal';
import VerificationBadge from '../Verification/VerificationBadge';
import ResumeUpload from '../Auth/ResumeUpload';
import { profileService, UserProfile, UpdateProfileData } from '../../services/profile.service';
import './ProfileSettings.css';

interface ProfileSettingsProps {
  darkMode?: boolean;
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ darkMode = false }) => {
  const { user, isAuthenticated, verificationStatus, refreshVerificationStatus } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'verification' | 'preferences'>('profile');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [, setProfileData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Form state
  const [fullName, setFullName] = useState('');
  const [headline, setHeadline] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationCountry, setLocationCountry] = useState('');

  // Notification preferences
  const [notifReferralStatus, setNotifReferralStatus] = useState(() =>
    localStorage.getItem('pref_notif_referral_status') !== 'false'
  );
  const [notifNewReferrals, setNotifNewReferrals] = useState(() =>
    localStorage.getItem('pref_notif_new_referrals') !== 'false'
  );
  const [notifMarketing, setNotifMarketing] = useState(() =>
    localStorage.getItem('pref_notif_marketing') === 'true'
  );

  useEffect(() => {
    if (isAuthenticated) loadProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const data = await profileService.getProfile();
      setProfileData(data);
      setFullName(data.fullName || '');
      setHeadline(data.headline || '');
      setLocationCity(data.location?.city || '');
      setLocationCountry(data.location?.country || '');
    } catch {
      setFullName(user?.name || '');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setSuccessMessage(null);
    try {
      const updates: UpdateProfileData = {
        fullName,
        headline,
        location: { city: locationCity, country: locationCountry },
      };
      await profileService.updateProfile(updates);
      setSuccessMessage('Profile updated successfully.');
    } catch {
      setSuccessMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = () => {
    localStorage.setItem('pref_notif_referral_status', String(notifReferralStatus));
    localStorage.setItem('pref_notif_new_referrals', String(notifNewReferrals));
    localStorage.setItem('pref_notif_marketing', String(notifMarketing));
    setSuccessMessage('Preferences saved.');
  };

  if (!isAuthenticated) {
    return <div className={`profile-settings ${darkMode ? 'dark' : ''}`}><p>Please sign in to view your profile.</p></div>;
  }

  return (
    <div className={`profile-settings ${darkMode ? 'dark' : ''}`}>
      <div className="profile-header">
        <div className="profile-avatar-section">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.name} className="profile-avatar-large" />
          ) : (
            <div className="profile-avatar-large placeholder">{user?.name?.charAt(0) || 'U'}</div>
          )}
          <div>
            <h2>{user?.name || 'User'}</h2>
            <p className="profile-email">{user?.email}</p>
            {verificationStatus?.is_verified && <VerificationBadge companyName={verificationStatus.company_name || ''} />}
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        <button className={activeTab === 'profile' ? 'active' : ''} onClick={() => setActiveTab('profile')}>Profile</button>
        <button className={activeTab === 'verification' ? 'active' : ''} onClick={() => setActiveTab('verification')}>Verification</button>
        <button className={activeTab === 'preferences' ? 'active' : ''} onClick={() => setActiveTab('preferences')}>Preferences</button>
      </div>

      {successMessage && <div className="profile-message">{successMessage}</div>}

      {activeTab === 'profile' && (
        <div className="profile-tab-content">
          <h3>Personal Information</h3>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="profile-form">
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Headline</label>
                <input type="text" value={headline} onChange={e => setHeadline(e.target.value)} placeholder="e.g. Senior Engineer at Stripe" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input type="text" value={locationCity} onChange={e => setLocationCity(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input type="text" value={locationCountry} onChange={e => setLocationCountry(e.target.value)} />
                </div>
              </div>
              <button className="btn-primary" onClick={handleSaveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          <div className="profile-resume-section">
            <h3>Resume</h3>
            <ResumeUpload darkMode={darkMode} hideHeader />
          </div>
        </div>
      )}

      {activeTab === 'verification' && (
        <div className="profile-tab-content">
          <h3>Company Verification</h3>
          <p>Verify your employment to get a trust badge on your vacancy posts.</p>
          {verificationStatus?.is_verified ? (
            <div className="verification-status verified">
              <VerificationBadge companyName={verificationStatus.company_name || ''} />
              <p>You are verified at <strong>{verificationStatus.company_name}</strong>.</p>
            </div>
          ) : (
            <div className="verification-status">
              <p>You are not yet verified. Verify your company email to earn a trust badge.</p>
              <button className="btn-primary" onClick={() => setShowVerificationModal(true)}>
                Verify Now
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'preferences' && (
        <div className="profile-tab-content">
          <h3>Notification Preferences</h3>
          <div className="preferences-list">
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={notifReferralStatus}
                onChange={e => setNotifReferralStatus(e.target.checked)}
              />
              <div className="preference-info">
                <span className="preference-label">Referral status updates</span>
                <span className="preference-desc">Get notified when your referral status changes</span>
              </div>
            </label>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={notifNewReferrals}
                onChange={e => setNotifNewReferrals(e.target.checked)}
              />
              <div className="preference-info">
                <span className="preference-label">New referrals on your vacancies</span>
                <span className="preference-desc">Get notified when someone refers a candidate to your vacancy</span>
              </div>
            </label>
            <label className="preference-toggle">
              <input
                type="checkbox"
                checked={notifMarketing}
                onChange={e => setNotifMarketing(e.target.checked)}
              />
              <div className="preference-info">
                <span className="preference-label">Marketing emails</span>
                <span className="preference-desc">Receive product updates, tips, and promotional offers</span>
              </div>
            </label>
          </div>
          <button className="btn-primary" onClick={handleSavePreferences} style={{ marginTop: '16px' }}>
            Save Preferences
          </button>
        </div>
      )}

      {showVerificationModal && (
        <CompanyVerificationModal
          isOpen={showVerificationModal}
          onClose={() => { setShowVerificationModal(false); refreshVerificationStatus(); }}
          onSuccess={() => { setShowVerificationModal(false); refreshVerificationStatus(); }}
        />
      )}
    </div>
  );
};

export default ProfileSettings;
